import { useState, useRef, useEffect } from "react";
import { Link } from "react-router";
import { trpc } from "../../lib/trpc";
import { useTheme, type DashboardThemeMode } from "../../context/ThemeContext";
import { mapSubscriptionToProductTier } from "@profolio/validators/tiers";
import {
  CreditCard,
  FileText,
  LayoutDashboard,
  ShieldAlert,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

interface AccountMenuProps {
  align?: "left" | "right";
  className?: string;
}

export function AccountMenu({ align = "right", className = "" }: AccountMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { data: session, isLoading } = trpc.auth.me.useQuery();
  const { data: profile } = trpc.profile.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    refetchInterval: 3000,
  });
  const { data: subscription } = trpc.billing.getMySubscription.useQuery(undefined, {
    enabled: !!session,
    refetchInterval: 3000,
  });

  const { themeMode, setThemeMode } = useTheme();

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      setIsOpen(false);
      window.location.href = "/login";
    },
    onError: () => {
      setIsOpen(false);
      toast.error("Failed to logout");
    },
  });

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (isLoading || !session) {
    return (
      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center animate-pulse" />
    );
  }

  const isSubscriptionActive = subscription?.status === "active" && (!subscription.expiresAt || new Date(subscription.expiresAt) >= new Date());
  const rawTier = (isSubscriptionActive ? subscription?.tier : null) || (profile as any)?.userTier || "starter";
  const productTier = mapSubscriptionToProductTier(rawTier);
  const tierName =
    productTier === "premium"
      ? "Premium"
      : productTier === "pro"
      ? "Pro"
      : "Starter Plan";

  const isPro = productTier === "pro" || productTier === "premium";
  const initials = (profile?.fullName || session.email || "U")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isAdmin = session.role === "admin";

  return (
    <div className={`relative inline-block text-left ${className}`}>
      {/* Trigger Button — sleek avatar circle */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        id="account-menu-trigger"
        className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 border border-primary/25 text-primary font-bold text-[10px] hover:ring-2 hover:ring-primary/30 transition-all cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
        title={profile?.fullName || session.email}
      >
        {initials}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="account-menu-trigger"
          className={`absolute ${
            align === "right" ? "right-0" : "left-0"
          } mt-2 w-64 rounded-xl border border-border bg-card text-card-foreground shadow-lg z-50 py-1.5 focus:outline-none animate-in fade-in-50 zoom-in-95 duration-100`}
        >
          {/* User Info Header */}
          <div className="px-3.5 py-2.5 border-b border-border">
            <p className="text-xs font-bold text-foreground truncate">
              {profile?.fullName || "Profolio User"}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">{session.email}</p>
            <div className="mt-2 flex items-center justify-between">
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  isPro
                    ? "bg-amber-100 text-amber-900 dark:bg-amber-950/80 dark:text-amber-200"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {isPro && <Sparkles className="h-2.5 w-2.5 text-amber-500" />}
                {tierName}
              </span>
              <Link
                to="/profile/billing"
                onClick={() => setIsOpen(false)}
                className="text-[11px] font-semibold text-primary hover:underline"
              >
                {isPro ? "Manage Plan" : "Upgrade →"}
              </Link>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="py-1">
            <Link
              to="/profile/edit"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground hover:bg-muted transition-colors"
            >
              <LayoutDashboard className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Portfolio Editor</span>
            </Link>

            <Link
              to="/profile/cv"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground hover:bg-muted transition-colors"
            >
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Academic CV Builder</span>
            </Link>

            <Link
              to="/profile/billing"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-foreground hover:bg-muted transition-colors"
            >
              <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Billing & Subscriptions</span>
            </Link>

            {isAdmin && (
              <Link
                to="/admin"
                role="menuitem"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors"
              >
                <ShieldAlert className="h-3.5 w-3.5" />
                <span>Admin Console</span>
              </Link>
            )}
          </div>

          {/* Theme Selector */}
          <div className="border-t border-border px-3.5 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1.5">
              Dashboard Theme
            </span>
            <div className="grid grid-cols-3 gap-1 bg-muted p-1 rounded-lg text-xs">
              {(
                [
                  { id: "light", label: "Light", icon: Sun },
                  { id: "dark", label: "Dark", icon: Moon },
                  { id: "system", label: "Auto", icon: Monitor },
                ] as const
              ).map((mode) => {
                const Icon = mode.icon;
                const isSelected = themeMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setThemeMode(mode.id as DashboardThemeMode)}
                    className={`flex items-center justify-center gap-1 py-1 px-1.5 rounded text-[11px] font-medium transition-all ${
                      isSelected
                        ? "bg-background text-foreground shadow-xs font-semibold"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Logout Action */}
          <div className="border-t border-border pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                logoutMutation.mutate();
              }}
              disabled={logoutMutation.isPending}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-xs text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            >
              {logoutMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <LogOut className="h-3.5 w-3.5" />
              )}
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
